import { CollapsibleBox } from "@/components/CollapsibleBox";
import {
  historyActorLabel,
  historyReasonLabel,
  historyStatusLabel,
  readBondHistory,
  type BondHistoryRow,
} from "@/lib/bondLifecycle";
import { historyEventLabel } from "@/lib/bondStateModel";
import { formatHistoryStamp } from "@/lib/listFormat";

// BRK-S427, BRK-S502 — the submission's "History" box (on its tab and the
// Submission Profile): Event · Updated by (User / Signal / System) ·
// Timestamp ("10/14/2026 · 9:19:05 AM MT"), over the order's `history[]`,
// newest first. A row is either
// an EVENT (a GIA sent, an upload, a rider request — `event`, ahead of
// PMS-S841) or a status move, which keeps the status history's wording
// (BRK-S323). Every column is a closed map with a neutral fallback, so a value
// this build has not mirrored still reads as something, never a blank.

interface HistoryLine {
  row: BondHistoryRow;
  /** The event's sentence, or null for a status move. */
  event: string | null;
}

/** Each entry through the shared reader (which drops what it cannot read),
 *  labelling its event here: the reader reads an event it does not recognise
 *  as null, but that row is still an "Update", not the status it happened
 *  under. */
function readLines(history: unknown): HistoryLine[] {
  if (!Array.isArray(history)) return [];
  const lines: HistoryLine[] = [];
  for (const raw of history as unknown[]) {
    const [row] = readBondHistory([raw]);
    if (row === undefined) continue;
    const served =
      typeof raw === "object" && raw !== null && "event" in raw
        ? raw.event
        : undefined;
    lines.push({
      row,
      event:
        row.event != null || typeof served === "string"
          ? historyEventLabel(row.event ?? null)
          : null,
    });
  }
  // ISO timestamps sort as text; newest first.
  return lines.sort((a, b) =>
    a.row.at < b.row.at ? 1 : a.row.at > b.row.at ? -1 : 0,
  );
}

const CELL = "block sm:table-cell sm:px-3 sm:py-2 sm:align-top";

export function SubmissionHistoryPanel({ history }: { history: unknown }) {
  const lines = readLines(history);
  return (
    <CollapsibleBox title="History">
      {lines.length === 0 ? (
        <p className="text-sm text-brk-dim">No history yet.</p>
      ) : (
        // Two lines per row below sm (the event, then who and when), a table
        // from sm. Explicit roles: a row restyled as a block keeps its table
        // semantics in every screen reader.
        <table
          role="table"
          aria-label="Submission history"
          className="block w-full text-sm sm:table"
        >
          <thead
            role="rowgroup"
            className="sr-only sm:not-sr-only sm:table-header-group"
          >
            <tr
              role="row"
              className="border-b border-brk-border text-left text-xs text-brk-dim"
            >
              <th
                role="columnheader"
                scope="col"
                className="px-3 py-2 font-medium"
              >
                Event
              </th>
              <th
                role="columnheader"
                scope="col"
                className="px-3 py-2 font-medium"
              >
                Updated by
              </th>
              <th
                role="columnheader"
                scope="col"
                className="px-3 py-2 font-medium"
              >
                Timestamp
              </th>
            </tr>
          </thead>
          <tbody
            role="rowgroup"
            className="flex flex-col divide-y divide-brk-border sm:table-row-group"
          >
            {lines.map(({ row, event }, i) => {
              const reason =
                event === null ? historyReasonLabel(row.reason) : null;
              return (
                <tr
                  role="row"
                  key={`${row.at}-${String(i)}`}
                  className="flex flex-wrap gap-x-2 py-2 sm:table-row sm:py-0"
                >
                  <td
                    role="cell"
                    className={`${CELL} w-full min-w-0 break-words sm:w-auto`}
                  >
                    <p className="font-medium text-brk-text">
                      {event ?? historyStatusLabel(row.to)}
                    </p>
                    {reason && <p className="text-brk-dim">{reason}</p>}
                  </td>
                  <td
                    role="cell"
                    className={`${CELL} text-brk-dim sm:whitespace-nowrap`}
                  >
                    {historyActorLabel(row.actorKind) ?? "—"}
                  </td>
                  <td
                    role="cell"
                    className={`${CELL} text-brk-dim before:mr-2 before:content-['·'] sm:whitespace-nowrap sm:before:content-none`}
                  >
                    {formatHistoryStamp(row.at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </CollapsibleBox>
  );
}
